import { type Browser } from 'igv';
import { useCallback, useRef, useState } from 'react';

// igv display settings persisted across page loads, grouped by "scope": the
// reads (alignment) track shares one scope regardless of BAM/CRAM, every other
// track is scoped by file format (so all BED tracks share one set of settings).
// Add an option key to make a setting savable, or a new entry to persist another
// format. Never list data-source (name/url/indexURL) or locus-specific (sort)
// keys: those belong to a track's data and must not carry across runs.
const ANNOTATION_SETTINGS = ['displayMode', 'color'] as const;

const SETTINGS_BY_SCOPE: Record<string, readonly string[]> = {
  alignment: [
    'showSoftClips',
    'viewAsPairs',
    'showAllBases',
    'showMismatches',
    'displayMode',
    'colorBy',
    'groupBy',
  ],
  bed: ANNOTATION_SETTINGS,
  gff: ANNOTATION_SETTINGS,
  gff3: ANNOTATION_SETTINGS,
  gtf: ANNOTATION_SETTINGS,
  narrowpeak: ANNOTATION_SETTINGS,
  broadpeak: ANNOTATION_SETTINGS,
  refgene: ANNOTATION_SETTINGS,
  vcf: ['displayMode', 'colorBy'],
  wig: ['autoscale', 'displayMode', 'color'],
  bedgraph: ['autoscale', 'displayMode', 'color'],
  seg: ['displayMode'],
  gwas: ['dotSize'],
};

// Browser-level (non-track) settings igv serializes in its session.
const BROWSER_SETTING_KEYS = ['showSampleNames'] as const;

type IgvScopeSettings = Record<string, unknown>;

export type IgvPrefs = {
  browser: IgvScopeSettings;
  scopes: Record<string, IgvScopeSettings>;
};

export const IGV_PREFS_STORAGE_KEY = 'igv_prefs';

const readStoredPrefs = (): IgvPrefs => {
  const storedPrefs = localStorage.getItem(IGV_PREFS_STORAGE_KEY);
  if (!storedPrefs) {
    return { browser: {}, scopes: {} };
  }
  try {
    const parsed = JSON.parse(storedPrefs);
    return { browser: parsed.browser ?? {}, scopes: parsed.scopes ?? {} };
  } catch {
    // Corrupt/outdated value: fall back to defaults rather than throwing in render.
    return { browser: {}, scopes: {} };
  }
};

// Falls back to the filename extension for user-added local files, whose format
// igv only infers once loaded (i.e. after we apply prefs at load time).
const findTrackScope = (track: Record<string, unknown>): string | null => {
  const format = typeof track.format === 'string' ? track.format.toLowerCase() : undefined;
  if (track.type === 'alignment' || format === 'bam' || format === 'cram') {
    return 'alignment';
  }
  if (format && format in SETTINGS_BY_SCOPE) {
    return format;
  }
  const name = typeof track.name === 'string' ? track.name : undefined;
  const extension = name?.includes('.') ? name.split('.').pop()?.toLowerCase() : undefined;
  return extension && extension in SETTINGS_BY_SCOPE ? extension : null;
};

const pickScopeSettings = (scope: string, source: Record<string, unknown>): IgvScopeSettings =>
  Object.fromEntries(
    SETTINGS_BY_SCOPE[scope].filter((key) => key in source).map((key) => [key, source[key]])
  );

// toJSON serializes only settings the user changed from their defaults.
type IgvSession = Record<string, unknown> & { tracks?: Record<string, unknown>[] };

// Read current settings from the live browser, merged onto what's stored. Only
// scopes present in this session are updated, so a browser that never showed a
// BED track won't wipe stored BED prefs. If several tracks share a scope, the
// last one in the session wins.
const capturePrefs = (browser: Browser): IgvPrefs => {
  const session = browser.toJSON() as unknown as IgvSession;
  const previous = readStoredPrefs();

  const scopes = { ...previous.scopes };
  (session.tracks ?? []).forEach((track) => {
    const scope = findTrackScope(track);
    if (scope) {
      scopes[scope] = pickScopeSettings(scope, track);
    }
  });

  // The browser always exists in the session (unlike track scopes), so rebuild
  // its settings rather than merge: merging would keep a stale value when the
  // user reverts a setting to its default, which toJSON then omits.
  const browserSettings = Object.fromEntries(
    BROWSER_SETTING_KEYS.filter((key) => key in session).map((key) => [key, session[key]])
  );

  return { browser: browserSettings, scopes };
};

// Merge a track's stored scope settings into its config. The result is asserted
// back to the input type: the settings are valid igv options, but igv's types
// (e.g. colorBy's discriminated union) reject them spread in independently.
export const applyTrackPrefs = <TrackConfig extends object>(
  track: TrackConfig,
  prefs: IgvPrefs
): TrackConfig => {
  const scope = findTrackScope(track as Record<string, unknown>);
  if (!scope) {
    return track;
  }
  const scopeSettings = prefs.scopes[scope];
  return scopeSettings ? ({ ...track, ...scopeSettings } as TrackConfig) : track;
};

// Exposes the stored prefs and a `persistPrefs` callback. igv mutates settings on
// its own track instances (outside React), so we read them back at save time
// rather than tracking each change; the write is synchronous so it survives a
// reload. Prefs live in a ref, not state, since they're only read when creating
// the browser or loading a track — saving shouldn't trigger a re-render.
export const useIgvPrefs = () => {
  const [initialPrefs] = useState<IgvPrefs>(readStoredPrefs);
  const prefsRef = useRef(initialPrefs);

  const persistPrefs = useCallback((browser: Browser) => {
    const captured = capturePrefs(browser);
    localStorage.setItem(IGV_PREFS_STORAGE_KEY, JSON.stringify(captured));
    prefsRef.current = captured;
  }, []);

  return { prefsRef, persistPrefs };
};
